/**
 * Shared UI vocabulary: header/nav, generic actions, shared components
 * (Modal, ConfirmDialog, DataTable pager, toasts, empty/error states).
 */
const en = {
  // Navigation (AppSidebar)
  'nav.chat': 'Chat',
  'nav.myAgents': 'My Agents',
  'nav.analytics': 'Analytics',
  'nav.feedback': 'Feedback',
  'nav.users': 'Users',
  'nav.departments': 'Departments',
  'nav.manageAgents': 'Manage Agents',
  'nav.dataGovernance': 'Data Governance',
  'nav.signOut': 'Sign Out',
  'nav.menu': 'Menu',
  'nav.main': 'Main',
  'nav.switchToArabic': 'Switch language to Arabic',
  'nav.switchToEnglish': 'Switch language to English',
  'nav.lightMode': 'Switch to light mode',
  'nav.darkMode': 'Switch to dark mode',
  'nav.accountMenu': 'Account menu for {name}',
  'nav.commandPalette': 'Open command palette',
  'nav.commandPaletteHint': 'Command palette (Ctrl/⌘ K)',
  'nav.version': 'Version {v}',
  'nav.sidebar': 'Sidebar',
  'nav.admin': 'Admin',
  'nav.collapseSidebar': 'Collapse sidebar',
  'nav.expandSidebar': 'Expand sidebar',
  'nav.closeMenu': 'Close menu',

  // Generic actions
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.retry': 'Retry',
  'common.search': 'Search',
  'common.clearSearch': 'Clear search',
  'common.clearFilters': 'Clear filters',
  'common.loading': 'Loading',
  'common.downloadCsv': 'Download CSV',
  'common.csv': 'CSV',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.active': 'Active',
  'common.inactive': 'Inactive',
  'common.all': 'All',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.never': 'Never',
  'common.actions': 'Actions',
  'common.required': 'required',

  // Relative time
  'time.minutesAgo': '{n}m ago',
  'time.hoursAgo': '{n}h ago',
  'time.daysAgo': '{n}d ago',

  // Modal unsaved-changes guard
  'modal.discardTitle': 'Discard changes?',
  'modal.discardMessage': 'You have unsaved changes in this form.',
  'modal.discard': 'Discard',
  'modal.keepEditing': 'Keep editing',

  // ConfirmDialog
  'confirm.confirm': 'Confirm',
  'confirm.working': 'Working…',

  // DataTable pager
  'table.pagination': 'Pagination',
  'table.showing': 'Showing {from}–{to} of {total} {noun}',
  'table.previousPage': 'Previous page',
  'table.nextPage': 'Next page',

  // Toasts
  'toast.dismiss': 'Dismiss notification',

  // Voice input (useSpeechToText)
  'speech.unsupported': 'Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari.',
  'speech.denied': 'Microphone access was denied. Please enable microphone permissions in your browser settings.',
  'speech.network': 'Network error. Speech recognition requires an internet connection in Chrome.',
  'speech.noMic': 'No microphone found. Please connect a microphone and try again.',
  'speech.unavailable': 'Speech recognition service is not available. Please try again later.',
  'speech.generic': 'Speech recognition error: {code}',
  'speech.failed': 'Failed to start speech recognition. Please try again.',
};

const ar: Record<keyof typeof en, string> = {
  'nav.chat': 'المحادثة',
  'nav.myAgents': 'وكلائي',
  'nav.analytics': 'التحليلات',
  'nav.feedback': 'التقييمات',
  'nav.users': 'المستخدمون',
  'nav.departments': 'الأقسام',
  'nav.manageAgents': 'إدارة الوكلاء',
  'nav.dataGovernance': 'حوكمة البيانات',
  'nav.signOut': 'تسجيل الخروج',
  'nav.menu': 'القائمة',
  'nav.main': 'الرئيسية',
  'nav.switchToArabic': 'التبديل إلى العربية',
  'nav.switchToEnglish': 'التبديل إلى الإنجليزية',
  'nav.lightMode': 'التبديل إلى الوضع الفاتح',
  'nav.darkMode': 'التبديل إلى الوضع الداكن',
  'nav.accountMenu': 'قائمة الحساب لـ {name}',
  'nav.commandPalette': 'فتح لوحة الأوامر',
  'nav.commandPaletteHint': 'لوحة الأوامر (Ctrl/⌘ K)',
  'nav.version': 'الإصدار {v}',
  'nav.sidebar': 'الشريط الجانبي',
  'nav.admin': 'الإدارة',
  'nav.collapseSidebar': 'طي الشريط الجانبي',
  'nav.expandSidebar': 'توسيع الشريط الجانبي',
  'nav.closeMenu': 'إغلاق القائمة',

  'common.save': 'حفظ',
  'common.saving': 'جارٍ الحفظ…',
  'common.cancel': 'إلغاء',
  'common.close': 'إغلاق',
  'common.delete': 'حذف',
  'common.edit': 'تعديل',
  'common.retry': 'إعادة المحاولة',
  'common.search': 'بحث',
  'common.clearSearch': 'مسح البحث',
  'common.clearFilters': 'مسح عوامل التصفية',
  'common.loading': 'جارٍ التحميل',
  'common.downloadCsv': 'تنزيل CSV',
  'common.csv': 'CSV',
  'common.copy': 'نسخ',
  'common.copied': 'تم النسخ',
  'common.active': 'نشط',
  'common.inactive': 'غير نشط',
  'common.all': 'الكل',
  'common.yes': 'نعم',
  'common.no': 'لا',
  'common.never': 'أبداً',
  'common.actions': 'الإجراءات',
  'common.required': 'مطلوب',

  'time.minutesAgo': 'قبل {n} د',
  'time.hoursAgo': 'قبل {n} س',
  'time.daysAgo': 'قبل {n} يوم',

  'modal.discardTitle': 'تجاهل التغييرات؟',
  'modal.discardMessage': 'لديك تغييرات غير محفوظة في هذا النموذج.',
  'modal.discard': 'تجاهل',
  'modal.keepEditing': 'متابعة التعديل',

  'confirm.confirm': 'تأكيد',
  'confirm.working': 'جارٍ التنفيذ…',

  'table.pagination': 'التنقل بين الصفحات',
  'table.showing': 'عرض {from}–{to} من {total} {noun}',
  'table.previousPage': 'الصفحة السابقة',
  'table.nextPage': 'الصفحة التالية',

  'toast.dismiss': 'إغلاق الإشعار',

  'speech.unsupported': 'التعرّف على الصوت غير مدعوم في هذا المتصفح. يُرجى استخدام Chrome أو Edge أو Safari.',
  'speech.denied': 'تم رفض الوصول إلى الميكروفون. يُرجى تفعيل إذن الميكروفون من إعدادات المتصفح.',
  'speech.network': 'خطأ في الشبكة. يتطلب التعرّف على الصوت في Chrome اتصالاً بالإنترنت.',
  'speech.noMic': 'لم يتم العثور على ميكروفون. يُرجى توصيل ميكروفون والمحاولة مرة أخرى.',
  'speech.unavailable': 'خدمة التعرّف على الصوت غير متاحة حالياً. يُرجى المحاولة لاحقاً.',
  'speech.generic': 'خطأ في التعرّف على الصوت: {code}',
  'speech.failed': 'تعذّر بدء التعرّف على الصوت. يُرجى المحاولة مرة أخرى.',
};

const common = { en, ar };
export default common;
